import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Chip, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, FormControl, InputLabel, Select, MenuItem, TextField,
    Alert, CircularProgress, IconButton, Tooltip, Paper,
    TableSortLabel, Collapse, Button, Divider, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButton, ToggleButtonGroup, LinearProgress,
    Accordion, AccordionSummary, AccordionDetails,
    Badge, Tab, Tabs
} from '@mui/material';
import {
    CheckCircle as ValidatedIcon,
    Cancel as RejectedIcon,
    Edit as StatusChangeIcon,
    Visibility as ViewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Person as UserIcon,
    AccessTime as TimeIcon,
    FilterList as FilterIcon,
    Assessment as StatsIcon,
    Download as ExportIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    TrendingUp as TrendingUpIcon,
    Today as TodayIcon,
    DateRange as DateRangeIcon,
    BarChart as BarChartIcon,
    ViewList as TableViewIcon,
    ViewModule as CardViewIcon,
    Info as InfoIcon,
    Edit as EditIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import listingValidationService from '../../services/listingValidationService';

/**
 * Composant d'historique des annonces - Version Administrative Optimisée
 * Interface complète pour suivre toutes les actions administratives sur les annonces
 */
const ListingHistoryNew = () => {
    // ==================== ÉTATS ====================
    const [history, setHistory] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Filtres avancés
    const [filters, setFilters] = useState({
        action: '',
        status: '',
        adminFilter: '',
        ownerId: '',
        listingId: '',
        dateFrom: '',
        dateTo: ''
    });
    
    // États UI
    const [viewMode, setViewMode] = useState('table');
    const [showFilters, setShowFilters] = useState(false);
    const [showStats, setShowStats] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState('30');
    const [tabValue, setTabValue] = useState(0);
    
    // Tri
    const [orderBy, setOrderBy] = useState('createdAt');
    const [order, setOrder] = useState('desc');
    
    // Dialog pour les détails
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [detailDialog, setDetailDialog] = useState(false);
    
    const { showNotification } = useNotifications();

    // ==================== FONCTIONS API ====================
    
    /**
     * Charger l'historique avec les filtres actuels
     */
    const loadHistory = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const response = await listingValidationService.getListingHistory({
                page,
                limit: 20,
                orderBy,
                order,
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            });
            
            if (response.success) {
                setHistory(response.history);
                setTotalPages(response.pagination.pages);
                setTotalRecords(response.pagination.total);
                setCurrentPage(page);
                console.log('[ListingHistory] Loaded', response.history.length, 'entries');
            } else {
                showNotification('Erreur lors du chargement de l\'historique', 'error');
            }
        } catch (error) {
            console.error('[ListingHistory] Error loading history:', error);
            if (error.response?.status === 403) {
                showNotification('Accès non autorisé', 'error');
            } else {
                showNotification('Erreur lors du chargement de l\'historique', 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [filters, orderBy, order, showNotification]);

    /**
     * Charger les statistiques
     */
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const response = await listingValidationService.getHistoryStats({
                period: selectedPeriod,
                groupBy: 'day'
            });
            
            if (response.success) {
                setStats(response.stats);
                console.log('[ListingHistory] Stats loaded:', response.stats);
            }
        } catch (error) {
            console.error('[ListingHistory] Error loading stats:', error);
            showNotification('Erreur lors du chargement des statistiques', 'error');
        } finally {
            setStatsLoading(false);
        }
    }, [selectedPeriod, showNotification]);

    // ==================== EFFECTS ====================
    
    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        if (showStats) {
            loadStats();
        }
    }, [loadStats, showStats]);

    // ==================== HANDLERS ====================
    
    /**
     * Gérer le changement de page
     */
    const handlePageChange = (event, newPage) => {
        loadHistory(newPage);
    };

    /**
     * Gérer le tri des colonnes
     */
    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    /**
     * Gérer les changements de filtres
     */
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    /**
     * Réinitialiser tous les filtres
     */
    const clearFilters = () => {
        setFilters({
            action: '',
            status: '',
            adminFilter: '',
            ownerId: '',
            listingId: '',
            dateFrom: '',
            dateTo: ''
        });
    };

    /**
     * Gérer l'expansion des lignes
     */
    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    /**
     * Ouvrir les détails d'une entrée
     */
    const openDetails = (entry) => {
        setSelectedEntry(entry);
        setDetailDialog(true);
    };

    // ==================== HELPERS ====================
    
    /**
     * Formater la date pour affichage
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Obtenir l'icône selon l'action
     */
    const getActionIcon = (action) => {
        switch (action) {
            case 'validated':
                return <ValidatedIcon color="success" />;
            case 'rejected':
                return <RejectedIcon color="error" />;
            case 'status_changed':
                return <StatusChangeIcon color="warning" />;
            case 'updated':
                return <EditIcon color="info" />;
            case 'created':
                return <AddIcon color="primary" />;
            default:
                return <InfoIcon color="default" />;
        }
    };

    /**
     * Obtenir la couleur du chip selon l'action
     */
    const getActionColor = (action) => {
        switch (action) {
            case 'validated':
                return 'success';
            case 'rejected':
                return 'error';
            case 'status_changed':
                return 'warning';
            case 'updated':
                return 'info';
            case 'created':
                return 'primary';
            default:
                return 'default';
        }
    };

    /**
     * Obtenir le libellé de l'action
     */
    const getActionLabel = (action) => {
        const labels = {
            validated: 'Validée',
            rejected: 'Rejetée',
            status_changed: 'Statut modifié',
            updated: 'Mise à jour',
            created: 'Créée',
            deactivated: 'Désactivée',
            reactivated: 'Réactivée'
        };
        return labels[action] || action;
    };

    /**
     * Obtenir la couleur du statut
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'rejected':
                return 'error';
            case 'pending_verification':
                return 'warning';
            case 'inactive':
                return 'secondary';
            case 'draft':
                return 'default';
            default:
                return 'default';
        }
    };

    // ==================== COMPOSANTS DE RENDU ====================
    
    /**
     * Panneau de statistiques
     */
    const StatsPanel = () => (
        <Accordion expanded={showStats} onChange={() => setShowStats(!showStats)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <StatsIcon />
                    <Typography variant="h6">Statistiques</Typography>
                    <Chip 
                        label={`${selectedPeriod} derniers jours`} 
                        size="small" 
                        color="primary" 
                    />
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                {statsLoading ? (
                    <CircularProgress />
                ) : stats ? (
                    <Grid container spacing={2}>
                        {/* Statistiques générales */}
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <TrendingUpIcon color="primary" />
                                        <Box>
                                            <Typography variant="h4">{stats.totals.actions}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Actions totales
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <ViewIcon color="info" />
                                        <Box>
                                            <Typography variant="h4">{stats.totals.uniqueListings}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Annonces distinctes
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <UserIcon color="secondary" />
                                        <Box>
                                            <Typography variant="h4">{stats.totals.uniqueAdmins}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Admins actifs
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <BarChartIcon color="warning" />
                                        <Box>
                                            <Typography variant="h4">
                                                {Math.round(stats.totals.actions / parseInt(selectedPeriod))}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Actions/jour
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Actions par type */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Actions par type</Typography>
                                    <Stack spacing={1}>
                                        {stats.actions.map(action => (
                                            <Box key={action.action} display="flex" justifyContent="space-between" alignItems="center">
                                                <Chip 
                                                    label={getActionLabel(action.action)} 
                                                    color={getActionColor(action.action)} 
                                                    size="small" 
                                                />
                                                <Typography variant="body2" fontWeight="bold">
                                                    {action.count}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Admins les plus actifs */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Admins les plus actifs</Typography>
                                    <Stack spacing={1}>
                                        {stats.admins.slice(0, 5).map(admin => (
                                            <Box key={admin.admin.id} display="flex" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar sx={{ width: 24, height: 24 }}>
                                                        {admin.admin.name.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {admin.admin.name}
                                                    </Typography>
                                                </Stack>
                                                <Chip 
                                                    label={admin.count} 
                                                    size="small" 
                                                    color="primary" 
                                                />
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                ) : (
                    <Alert severity="info">Aucune donnée statistique disponible</Alert>
                )}
                
                {/* Contrôles de période */}
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Période d'analyse</Typography>
                    <ToggleButtonGroup
                        value={selectedPeriod}
                        exclusive
                        onChange={(e, value) => value && setSelectedPeriod(value)}
                        size="small"
                    >
                        <ToggleButton value="7">7 jours</ToggleButton>
                        <ToggleButton value="30">30 jours</ToggleButton>
                        <ToggleButton value="90">90 jours</ToggleButton>
                        <ToggleButton value="365">1 an</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </AccordionDetails>
        </Accordion>
    );

    /**
     * Panneau de filtres
     */
    const FiltersPanel = () => (
        <Accordion expanded={showFilters} onChange={() => setShowFilters(!showFilters)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterIcon />
                    <Typography variant="h6">Filtres</Typography>
                    {Object.values(filters).some(v => v) && (
                        <Badge badgeContent={Object.values(filters).filter(v => v).length} color="primary">
                            <Chip label="Actifs" size="small" />
                        </Badge>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                        <FormControl fullWidth>
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={filters.action}
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                            >
                                <MenuItem value="">Toutes</MenuItem>
                                <MenuItem value="validated">Validations</MenuItem>
                                <MenuItem value="rejected">Rejets</MenuItem>
                                <MenuItem value="status_changed">Changements de statut</MenuItem>
                                <MenuItem value="updated">Mises à jour</MenuItem>
                                <MenuItem value="created">Créations</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={4}>
                        <FormControl fullWidth>
                            <InputLabel>Statut</InputLabel>
                            <Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="">Tous</MenuItem>
                                <MenuItem value="active">Actif</MenuItem>
                                <MenuItem value="rejected">Rejeté</MenuItem>
                                <MenuItem value="pending_verification">En attente</MenuItem>
                                <MenuItem value="inactive">Inactif</MenuItem>
                                <MenuItem value="draft">Brouillon</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label="ID de l'annonce"
                            value={filters.listingId}
                            onChange={(e) => handleFilterChange('listingId', e.target.value)}
                            placeholder="ex: 123e4567-e89b-12d3-a456-426614174000"
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label="Date de début"
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            fullWidth
                            label="Date de fin"
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} md={6} lg={4}>
                        <Stack direction="row" spacing={1} height="100%" alignItems="center">
                            <Button 
                                variant="outlined" 
                                onClick={clearFilters}
                                startIcon={<ClearIcon />}
                            >
                                Réinitialiser
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={() => loadHistory(1)}
                                startIcon={<SearchIcon />}
                            >
                                Appliquer
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );

    /**
     * Vue en tableau
     */
    const TableView = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={orderBy === 'createdAt'}
                                direction={orderBy === 'createdAt' ? order : 'asc'}
                                onClick={() => handleSort('createdAt')}
                            >
                                Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Annonce</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Administrateur</TableCell>
                        <TableCell>Détails</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {history.map((entry) => (
                        <React.Fragment key={entry.id}>
                            <TableRow hover>
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatDate(entry.createdAt)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        icon={getActionIcon(entry.action)}
                                        label={getActionLabel(entry.action)}
                                        color={getActionColor(entry.action)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {entry.listing?.title || 'Annonce supprimée'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {entry.entityId}
                                        </Typography>
                                        {entry.listing?.owner && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Par: {entry.listing.owner.name}
                                            </Typography>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {entry.newStatus && (
                                        <Chip
                                            label={entry.newStatus}
                                            color={getStatusColor(entry.newStatus)}
                                            size="small"
                                        />
                                    )}
                                    {entry.previousStatus && entry.previousStatus !== entry.newStatus && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Avant: {entry.previousStatus}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {entry.performedBy ? (
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 24, height: 24 }}>
                                                {entry.performedBy.name.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2">
                                                    {entry.performedBy.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {entry.performedByRole}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Système
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {entry.comment && (
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200, 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {entry.comment}
                                        </Typography>
                                    )}
                                    {entry.changes && Object.keys(entry.changes).length > 0 && (
                                        <Chip 
                                            label={`${Object.keys(entry.changes).length} modification(s)`} 
                                            size="small" 
                                            color="info" 
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1}>
                                        <Tooltip title="Voir les détails">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => openDetails(entry)}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={expandedRows[entry.id] ? "Réduire" : "Développer"}>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => toggleRowExpansion(entry.id)}
                                            >
                                                {expandedRows[entry.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                            {expandedRows[entry.id] && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Collapse in={expandedRows[entry.id]}>
                                            <Box p={2} bgcolor="grey.50">
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Informations détaillées
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {entry.comment && (
                                                        <Grid item xs={12}>
                                                            <Paper sx={{ p: 2 }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Commentaire:
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {entry.comment}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}
                                                    {entry.changes && Object.keys(entry.changes).length > 0 && (
                                                        <Grid item xs={12}>
                                                            <Paper sx={{ p: 2 }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Modifications:
                                                                </Typography>
                                                                {Object.entries(entry.changes).map(([field, change]) => (
                                                                    <Box key={field} mb={1}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            <strong>{field}:</strong> {String(change.from || 'N/A')} → {String(change.to || 'N/A')}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Paper>
                                                        </Grid>
                                                    )}
                                                    {entry.metadata && (
                                                        <Grid item xs={12}>
                                                            <Paper sx={{ p: 2 }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Métadonnées:
                                                                </Typography>
                                                                <Typography variant="caption" display="block">
                                                                    IP: {entry.metadata.ip || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" display="block">
                                                                    Endpoint: {entry.metadata.endpoint || 'N/A'}
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // ==================== RENDU PRINCIPAL ====================
    
    return (
        <Box>
            {/* En-tête */}
            <Box mb={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4">
                        Historique des Annonces
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, value) => value && setViewMode(value)}
                            size="small"
                        >
                            <ToggleButton value="table">
                                <TableViewIcon />
                            </ToggleButton>
                            <ToggleButton value="cards">
                                <CardViewIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                        
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => loadHistory(currentPage)}
                        >
                            Actualiser
                        </Button>
                    </Stack>
                </Stack>
                
                <Typography variant="body1" color="text.secondary">
                    Suivi complet de toutes les actions administratives sur les annonces
                    {totalRecords > 0 && ` • ${totalRecords} entrées au total`}
                </Typography>
            </Box>

            {/* Panneau de statistiques */}
            <StatsPanel />
            
            {/* Panneau de filtres */}
            <Box mt={2}>
                <FiltersPanel />
            </Box>

            {/* Contenu principal */}
            <Box mt={3}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : history.length === 0 ? (
                    <Alert severity="info">
                        Aucune entrée d'historique trouvée avec les critères sélectionnés.
                    </Alert>
                ) : (
                    <>
                        <TableView />
                        
                        {/* Pagination */}
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    </>
                )}
            </Box>

            {/* Dialog de détails */}
            <Dialog 
                open={detailDialog} 
                onClose={() => setDetailDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Détails de l'action
                </DialogTitle>
                <DialogContent>
                    {selectedEntry && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">Action</Typography>
                                <Chip
                                    icon={getActionIcon(selectedEntry.action)}
                                    label={getActionLabel(selectedEntry.action)}
                                    color={getActionColor(selectedEntry.action)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">Date</Typography>
                                <Typography>{formatDate(selectedEntry.createdAt)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Annonce</Typography>
                                <Typography>{selectedEntry.listing?.title || 'Annonce supprimée'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ID: {selectedEntry.entityId}
                                </Typography>
                            </Grid>
                            {selectedEntry.performedBy && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">Effectué par</Typography>
                                    <Typography>{selectedEntry.performedBy.name} ({selectedEntry.performedBy.email})</Typography>
                                </Grid>
                            )}
                            {selectedEntry.comment && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">Commentaire</Typography>
                                    <Typography>{selectedEntry.comment}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog(false)}>
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListingHistoryNew;
